import {createSketchDimensionHistoryPatch} from '../../../history/dimension/SketchDimensionHistoryAdapter';
import type {SketchSolverRecordSource} from '../../../history/solver/SketchSolverRecordTypes';
import type {EntityPatch} from '../../../history/types';
import {
    createExistingHistorySnapshot,
    createMissingHistorySnapshot,
} from '../../../history/types';
import {
    SketchDimension,
    resolveSketchDimensionDefaultValueScale,
    resolveSketchDimensionQuantityKind,
} from '../model/SketchDimension';
import {SketchDimensionReference} from '../model/SketchDimensionReference';
import type {SketchDimensionIdentityService} from '../repository/SketchDimensionIdentityService';
import type {SketchDimensionRepository} from '../repository/SketchDimensionRepository';
import type {
    CreateSketchDimensionInput,
    SketchDimensionArcCondition,
    SketchDimensionBranchState,
    SketchDimensionDisplayState,
    SketchDimensionReferenceRecord,
    SketchDimensionRole,
    SketchDimensionSnapshot,
} from '../SketchDimensionTypes';
import {resolveChainAlignedLabelPositions} from '../resolveChainAlignedLabelPositions';
import type {SketchDimensionValidationService} from './SketchDimensionValidationService';

/** 尺寸历史事务提交输入。 */
export interface SketchDimensionTransactionInput {
    /** 事务标签（历史面板展示）。 */
    readonly label: string;
    /** 命令类型（`sketch.dimension.*`）。 */
    readonly commandType: string;
    /** 所属草图持久 ID。 */
    readonly sketchId: string;
    /** 尺寸持久 ID。 */
    readonly dimensionId: string;
    /** 本次事务的全部补丁（尺寸补丁必有，solver 补丁按需）。 */
    readonly patches: readonly EntityPatch[];
}

/**
 * 尺寸历史事务端口。
 *
 * 实现方必须把 `patches` 作为**一笔** Model History 事务原子提交
 * （02 计划 §8.1：dimension patch + solver patch 不可拆成两步）；提交失败必须抛错，
 * 由应用服务恢复仓库与 solver 运行时。
 */
export interface SketchDimensionHistoryTransactionPort {
    /**
     * 原子提交一笔尺寸事务。
     *
     * @param input 事务输入。
     */
    readonly commitTransaction: (input: SketchDimensionTransactionInput) => Promise<void>;
}

/** 尺寸操作关联的 solver 会话。 */
export interface SketchDimensionSolverSession {
    /**
     * 捕获 after 并输出 solver island 补丁。
     *
     * @returns solver 补丁；island 语义无变化时返回 null。
     */
    readonly commit: () => EntityPatch | null;
    /**
     * 恢复 solver 运行时到 before 并关闭会话。
     *
     * 说明：允许在 `commit()` 之后调用——事务提交失败时用于把已推进的
     * solver 运行时回滚到 before。
     */
    readonly rollback: () => void;
    /**
     * 本次求解后 island 内全部尺寸按新几何测得的业务值（核心单位；持久 ID → 值）。
     *
     * 用途：driving 尺寸改值/创建驱动几何后，同岛 driven 尺寸的显示值必须按新几何
     * 重新测量并随同一笔事务原子回写（否则 driven 仍显示旧值，且撤销/重做失配）。
     * driven 值是几何派生量，测量权威唯一（求解内核 `measureSketchDimensionOnSystem`），
     * 此处只搬运结果、不另起测量。无 solver 联动的会话可省略。
     */
    readonly measuredValues?: ReadonlyMap<string, number>;
}

/** 尺寸操作 solver 会话端口。 */
export interface SketchDimensionSolverSessionPort {
    /**
     * 开启一次尺寸操作的 solver 会话（捕获 before）。
     *
     * @param input 会话输入。
     * @returns solver 会话。
     */
    readonly begin: (input: {
        readonly sketchId: string;
        readonly dimensionId: string;
        readonly source: SketchSolverRecordSource;
    }) => SketchDimensionSolverSession;
}

/** 应用服务构造依赖。 */
export interface SketchDimensionApplicationServiceOptions {
    /** 尺寸仓库。 */
    readonly repository: SketchDimensionRepository;
    /** 身份服务。 */
    readonly identityService: SketchDimensionIdentityService;
    /** 校验服务。 */
    readonly validationService: SketchDimensionValidationService;
    /**
     * 历史事务端口。
     *
     * 说明：`createDimension`（自提交事务）必需；`createDimensionInBatch`
     * （父事务托管补丁）不需要，省略后只能走批量入口。
     */
    readonly transactionPort?: SketchDimensionHistoryTransactionPort;
    /** solver 会话端口；阶段 2 纯领域场景可缺省。 */
    readonly solverSessionPort?: SketchDimensionSolverSessionPort;
}

/**
 * 批量创建（父事务托管）结果。
 *
 * 说明：等距实体等复合命令在同一笔历史事务内同时提交几何与尺寸时使用；
 * 调用方必须把 `patches` 并入父事务原子提交，并在父事务失败时调用 `rollback`。
 */
export interface SketchDimensionInBatchCreationResult {
    /** 已写入仓库的尺寸快照。 */
    readonly snapshot: SketchDimensionSnapshot;
    /** 尺寸创建领域补丁（不含 solver 补丁，由父事务统一提交）。 */
    readonly patches: readonly EntityPatch[];
    /** 对称回滚仓库写入。 */
    readonly rollback: () => void;
}

/** 统一变更提交输入。 */
interface CommitMutationInput {
    /** 草图持久 ID。 */
    readonly sketchId: string;
    /** 尺寸持久 ID。 */
    readonly dimensionId: string;
    /** 事务标签。 */
    readonly label: string;
    /** 命令后缀（拼接为 `sketch.dimension.<suffix>`）。 */
    readonly commandSuffix: string;
    /** solver 记录来源。 */
    readonly source: SketchSolverRecordSource;
    /** 聚合变更函数。 */
    readonly mutate: (dimension: SketchDimension) => void;
}

/** 命令类型前缀。 */
const DIMENSION_COMMAND_PREFIX = 'sketch.dimension.';

/**
 * 草图尺寸应用服务。
 *
 * 设计说明：
 * 1. 每个公开操作对应操作矩阵（02 计划 §8.2）中的**一个** Model step：
 *    仓库变更 + solver 运行时变更组成一笔事务提交；
 * 2. 事务失败即整体回滚：仓库恢复 before 快照、solver 会话 rollback，
 *    不产生半截历史（HIST-002）；
 * 3. 服务不触碰渲染与 UI；预览/取消不经过本服务（不入栈语义由命令层保证）。
 */
export class SketchDimensionApplicationService {
    /** 构造依赖。 */
    private readonly options: SketchDimensionApplicationServiceOptions;

    /**
     * @param options 构造依赖。
     */
    public constructor(options: SketchDimensionApplicationServiceOptions) {
        this.options = options;
    }

    /**
     * 创建尺寸（一个 Model step）。
     *
     * @param input 创建输入。
     * @returns 创建后的尺寸快照。
     * @throws 当尺寸落库或历史提交失败时，回滚本次会话与已写入状态并原样重新抛出原异常。
     */
    public async createDimension(input: CreateSketchDimensionInput): Promise<SketchDimensionSnapshot> {
        if (!this.options.transactionPort) {
            throw new Error('尺寸历史事务端口未注入，无法提交创建；父事务托管场景请使用 createDimensionInBatch。');
        }

        const {snapshot, dimension} = this.buildDimensionRecord(input);
        const session = this.options.solverSessionPort?.begin({
            sketchId: input.sketchId,
            dimensionId: snapshot.dimensionId,
            source: 'dimension-added',
        }) ?? null;

        try {
            this.options.repository.create(dimension);
        } catch (error) {
            session?.rollback();
            throw error;
        }

        const createdSnapshot = dimension.toSnapshot();
        const patches: EntityPatch[] = [
            createSketchDimensionHistoryPatch({
                entityId: snapshot.dimensionId,
                operation: 'create',
                before: createMissingHistorySnapshot(),
                after: createExistingHistorySnapshot(createdSnapshot),
                metadata: {sketchId: input.sketchId},
            }),
        ];
        const solverPatch = session?.commit() ?? null;

        if (solverPatch) {
            patches.push(solverPatch);
        }

        // 新建 driving 驱动几何后同岛 driven 尺寸随同一笔事务原子重测回写。
        const remeasuredBefores = this.appendDrivenRemeasurePatches(input.sketchId, snapshot.dimensionId, session, patches);

        try {
            await this.options.transactionPort.commitTransaction({
                label: '新建尺寸',
                commandType: `${DIMENSION_COMMAND_PREFIX}create`,
                sketchId: input.sketchId,
                dimensionId: snapshot.dimensionId,
                patches,
            });
        } catch (error) {
            this.options.repository.delete(input.sketchId, snapshot.dimensionId);
            remeasuredBefores.forEach((snapshot) => this.options.repository.restoreDimension(snapshot));
            session?.rollback();
            throw error;
        }

        return createdSnapshot;
    }

    /**
     * 批量创建尺寸（父事务托管，一个 Model step 的组成部分）。
     *
     * 说明：
     * 1. 等距实体等复合命令需要在同一笔历史事务内原子提交几何与尺寸，
     *    本入口只做校验 + 落库 + 生成补丁，**不提交任何事务**；
     * 2. 调用方必须把返回的 `patches` 并入父事务（如
     *    `SketchRuntimeHistoryTransactionInput.resolveExtraPatches`），
     *    父事务失败时调用 `rollback` 对称回滚仓库写入；
     * 3. 本入口不产生 solver 补丁：尺寸进入仓库后，后续参数求解会通过
     *    尺寸补充端口（supplement provider）自动把 driving 尺寸编入求解岛。
     *
     * @param input 创建输入。
     * @returns 落库快照、领域补丁与对称回滚函数。
     * @throws 校验失败时抛出，仓库不产生任何写入。
     */
    public createDimensionInBatch(input: CreateSketchDimensionInput): SketchDimensionInBatchCreationResult {
        const {snapshot, dimension} = this.buildDimensionRecord(input);

        this.options.repository.create(dimension);

        const createdSnapshot = dimension.toSnapshot();
        const patches: EntityPatch[] = [
            createSketchDimensionHistoryPatch({
                entityId: snapshot.dimensionId,
                operation: 'create',
                before: createMissingHistorySnapshot(),
                after: createExistingHistorySnapshot(createdSnapshot),
                metadata: {sketchId: input.sketchId},
            }),
        ];

        return {
            snapshot: createdSnapshot,
            patches,
            rollback: (): void => {
                this.options.repository.delete(input.sketchId, snapshot.dimensionId);
            },
        };
    }

    /**
     * 校验输入并构建尺寸记录（身份分配 + 快照 + 模型实例）。
     *
     * 说明：`createDimension` 与 `createDimensionInBatch` 共用同一构建口径，
     * 保证两条入口的校验、命名、参数绑定行为完全一致。
     *
     * @param input 创建输入。
     * @returns 尺寸快照与模型实例。
     */
    private buildDimensionRecord(input: CreateSketchDimensionInput): {
        readonly snapshot: SketchDimensionSnapshot;
        readonly dimension: SketchDimension;
    } {
        const references = this.options.validationService.validateCreateInput(input);
        const repository = this.options.repository;
        const identity = this.options.identityService;
        const dimensionId = identity.allocateDimensionId();
        const quantityKind = resolveSketchDimensionQuantityKind(input.dimensionKind);
        const occupiedNames = new Set(
            repository.listBySketch(input.sketchId).map((dimension) => dimension.currentName),
        );
        const name = input.name?.trim() || identity.allocateName(occupiedNames);
        const snapshot: SketchDimensionSnapshot = {
            dimensionId,
            sketchId: input.sketchId,
            name,
            dimensionKind: input.dimensionKind,
            space: input.space ?? 'sketch-2d',
            role: input.role,
            suppressed: false,
            readOnly: false,
            parameter: {
                parameterId: identity.createParameterId(dimensionId, quantityKind),
                nominalValue: input.nominalValue,
                quantityKind,
                valueScale: input.valueScale ?? resolveSketchDimensionDefaultValueScale(input.dimensionKind),
                expression: null,
            },
            measuredValue: input.measuredValue ?? null,
            references: references.map((reference) => reference.toRecord()),
            branch: input.branch ?? {},
            display: input.display ?? {},
            solveState: 'unknown',
            repair: null,
            // 无元数据时省略字段，保证与旧快照（无该字段）的序列化形态一致
            ...(input.metadata ? {metadata: input.metadata} : {}),
        };

        return {
            snapshot,
            dimension: SketchDimension.fromSnapshot(snapshot),
        };
    }

    /**
     * 修改 driving 名义值（Modify 确认，一个 Model step）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param nominalValue 新名义值（核心单位）。
     * @param branch 同笔事务写入的分支状态（负值输入方向翻转，VAL-003）；省略不改分支。
     * @returns 变更后的尺寸快照。
     */
    public async changeNominalValue(
        sketchId: string,
        dimensionId: string,
        nominalValue: number,
        branch?: SketchDimensionBranchState,
    ): Promise<SketchDimensionSnapshot> {
        return this.commitMutation({
            sketchId,
            dimensionId,
            label: '修改尺寸值',
            commandSuffix: 'value-edit',
            source: 'dimension-updated',
            mutate: (dimension) => {
                dimension.changeNominalValue(nominalValue);
                if (branch !== undefined) {
                    dimension.changeBranch(branch);
                }
            },
        });
    }

    /**
     * 切换驱动角色（一个 Model step）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param role 目标角色。
     * @returns 变更后的尺寸快照。
     */
    public async changeRole(
        sketchId: string,
        dimensionId: string,
        role: SketchDimensionRole,
    ): Promise<SketchDimensionSnapshot> {
        return this.commitMutation({
            sketchId,
            dimensionId,
            label: '切换尺寸驱动角色',
            commandSuffix: 'role-change',
            source: 'dimension-role-changed',
            mutate: (dimension) => {
                dimension.changeRole(role);
            },
        });
    }

    /**
     * 删除尺寸（一个 Model step）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @returns 被删除尺寸的快照。
     * @throws 当尺寸删除或历史提交失败时，恢复已删尺寸、回滚本次会话并原样重新抛出原异常。
     */
    public async deleteDimension(sketchId: string, dimensionId: string): Promise<SketchDimensionSnapshot> {
        const repository = this.options.repository;
        const session = this.options.solverSessionPort?.begin({
            sketchId,
            dimensionId,
            source: 'dimension-deleted',
        }) ?? null;
        let before: SketchDimensionSnapshot;

        try {
            before = repository.delete(sketchId, dimensionId);
        } catch (error) {
            session?.rollback();
            throw error;
        }

        const patches: EntityPatch[] = [
            createSketchDimensionHistoryPatch({
                entityId: dimensionId,
                operation: 'delete',
                before: createExistingHistorySnapshot(before),
                after: createMissingHistorySnapshot(),
                metadata: {sketchId},
            }),
        ];
        const solverPatch = session?.commit() ?? null;

        if (solverPatch) {
            patches.push(solverPatch);
        }

        try {
            await this.options.transactionPort.commitTransaction({
                label: '删除尺寸',
                commandType: `${DIMENSION_COMMAND_PREFIX}delete`,
                sketchId,
                dimensionId,
                patches,
            });
        } catch (error) {
            repository.restoreDimension(before);
            session?.rollback();
            throw error;
        }

        return before;
    }

    /**
     * 重新附着引用（保留尺寸身份，一个 Model step）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param references 新引用记录列表。
     * @returns 变更后的尺寸快照。
     */
    public async reattachDimension(
        sketchId: string,
        dimensionId: string,
        references: readonly SketchDimensionReferenceRecord[],
    ): Promise<SketchDimensionSnapshot> {
        const referenceObjects = references.map((record) => new SketchDimensionReference(record));

        return this.commitMutation({
            sketchId,
            dimensionId,
            label: '重新附着尺寸',
            commandSuffix: 'reattach',
            source: 'dimension-updated',
            mutate: (dimension) => {
                dimension.reattach(referenceObjects);
            },
        });
    }

    /**
     * 修改圆系引用附着条件（一个 Model step）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param selectionOrder 目标引用选择顺序。
     * @param arcCondition 新附着条件。
     * @returns 变更后的尺寸快照。
     */
    public async changeArcCondition(
        sketchId: string,
        dimensionId: string,
        selectionOrder: number,
        arcCondition: SketchDimensionArcCondition,
    ): Promise<SketchDimensionSnapshot> {
        return this.commitMutation({
            sketchId,
            dimensionId,
            label: '修改尺寸附着条件',
            commandSuffix: 'arc-condition',
            source: 'dimension-updated',
            mutate: (dimension) => {
                dimension.changeArcCondition(selectionOrder, arcCondition);
            },
        });
    }

    /**
     * 设置抑制状态（一个 Model step）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param suppressed 是否抑制。
     * @returns 变更后的尺寸快照。
     */
    public async setSuppressed(
        sketchId: string,
        dimensionId: string,
        suppressed: boolean,
    ): Promise<SketchDimensionSnapshot> {
        return this.commitMutation({
            sketchId,
            dimensionId,
            label: suppressed ? '抑制尺寸' : '解除尺寸抑制',
            commandSuffix: 'suppress',
            source: 'dimension-updated',
            mutate: (dimension) => {
                dimension.setSuppressed(suppressed);
            },
        });
    }

    /**
     * 更新显示状态（一个 Model step）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param display 待合并显示字段。
     * @returns 变更后的尺寸快照。
     */
    public async updateDisplay(
        sketchId: string,
        dimensionId: string,
        display: SketchDimensionDisplayState,
    ): Promise<SketchDimensionSnapshot> {
        /**
         * 链尺寸组标注移动：整组共线联动（SW 语义——链的所有尺寸线同在一条轴上，
         * 拖动任意一条整组跟着挪），必须在**同一笔事务**内完成，否则 Undo 会把
         * 链拆散成"一条回去了、其余还在新轴上"。
         */
        if (display.labelPosition) {
            const chainResult = await this.tryCommitChainLabelDisplay(sketchId, dimensionId, display);

            if (chainResult) {
                return chainResult;
            }
        }

        return this.commitMutation({
            sketchId,
            dimensionId,
            label: '修改尺寸显示',
            commandSuffix: 'display',
            source: 'dimension-updated',
            mutate: (dimension) => {
                dimension.updateDisplay(display);
            },
        });
    }

    /**
     * 链尺寸组标注移动的原子提交（一笔事务改整组）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 被拖尺寸持久 ID。
     * @param display 被拖尺寸的待合并显示字段（含 labelPosition）。
     * @returns 被拖尺寸变更后的快照；非链成员/组内孤条时返回 null 走单条路径。
     */
    private async tryCommitChainLabelDisplay(
        sketchId: string,
        dimensionId: string,
        display: SketchDimensionDisplayState,
    ): Promise<SketchDimensionSnapshot | null> {
        const repository = this.options.repository;
        const dragged = repository.assert(sketchId, dimensionId).toSnapshot();
        const chainGroupId = dragged.branch.chainGroupId;

        if (!chainGroupId || !display.labelPosition) {
            return null;
        }

        const members = repository.listBySketch(sketchId)
            .map((dimension) => dimension.toSnapshot())
            .filter((snapshot) => snapshot.branch.chainGroupId === chainGroupId);

        if (members.length < 2) {
            return null;
        }

        const positions = resolveChainAlignedLabelPositions(members, dimensionId, display.labelPosition);

        if (!positions) {
            return null;
        }

        const befores: SketchDimensionSnapshot[] = [];
        const patches: EntityPatch[] = [];
        let draggedAfter: SketchDimensionSnapshot | null = null;

        try {
            members.forEach((before) => {
                const nextLabel = positions.get(before.dimensionId);

                if (!nextLabel) {
                    return;
                }

                const isDragged = before.dimensionId === dimensionId;
                const after = repository.mutate(sketchId, before.dimensionId, (dimension) => {
                    // 被拖条合并全部显示字段，其余成员只做法向共线对齐。
                    dimension.updateDisplay(isDragged ? display : {labelPosition: nextLabel});
                });

                if (isDragged) {
                    draggedAfter = after;
                }
                if (JSON.stringify(before) === JSON.stringify(after)) {
                    return;
                }
                befores.push(before);
                patches.push(createSketchDimensionHistoryPatch({
                    entityId: before.dimensionId,
                    operation: 'update',
                    before: createExistingHistorySnapshot(before),
                    after: createExistingHistorySnapshot(after),
                    metadata: {sketchId},
                }));
            });

            /** 语义无变化：不产生历史（HIST-002 空事务防护）。 */
            if (patches.length === 0) {
                return draggedAfter ?? dragged;
            }

            await this.options.transactionPort.commitTransaction({
                label: '修改尺寸显示',
                commandType: `${DIMENSION_COMMAND_PREFIX}display`,
                sketchId,
                dimensionId,
                patches,
            });
        } catch (error) {
            befores.forEach((snapshot) => repository.restoreDimension(snapshot));
            throw error;
        }

        return draggedAfter ?? dragged;
    }

    /**
     * 更新分支状态（一个 Model step）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param branch 新分支状态。
     * @returns 变更后的尺寸快照。
     */
    public async changeBranch(
        sketchId: string,
        dimensionId: string,
        branch: SketchDimensionBranchState,
    ): Promise<SketchDimensionSnapshot> {
        return this.commitMutation({
            sketchId,
            dimensionId,
            label: '修改尺寸分支',
            commandSuffix: 'branch',
            source: 'dimension-updated',
            mutate: (dimension) => {
                dimension.changeBranch(branch);
            },
        });
    }

    /**
     * 重命名尺寸（一个 Model step）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param name 新名称。
     * @returns 变更后的尺寸快照。
     */
    public async renameDimension(
        sketchId: string,
        dimensionId: string,
        name: string,
    ): Promise<SketchDimensionSnapshot> {
        return this.commitMutation({
            sketchId,
            dimensionId,
            label: '重命名尺寸',
            commandSuffix: 'rename',
            source: 'dimension-updated',
            mutate: (dimension) => {
                dimension.rename(name);
            },
        });
    }

    /**
     * 统一的“变更 + 原子事务”流程。
     *
     * @param input 变更提交输入。
     * @returns 变更后的尺寸快照。
     */
    private async commitMutation(input: CommitMutationInput): Promise<SketchDimensionSnapshot> {
        const repository = this.options.repository;
        const before = repository.assert(input.sketchId, input.dimensionId).toSnapshot();
        const session = this.options.solverSessionPort?.begin({
            sketchId: input.sketchId,
            dimensionId: input.dimensionId,
            source: input.source,
        }) ?? null;
        let after: SketchDimensionSnapshot;

        try {
            after = repository.mutate(input.sketchId, input.dimensionId, input.mutate);
        } catch (error) {
            session?.rollback();
            throw error;
        }

        const solverPatch = session?.commit() ?? null;

        if (JSON.stringify(before) === JSON.stringify(after) && !solverPatch) {
            /** 语义无变化：不产生历史（HIST-002 空事务防护）。 */
            session?.rollback();

            return after;
        }

        const patches: EntityPatch[] = [
            createSketchDimensionHistoryPatch({
                entityId: input.dimensionId,
                operation: 'update',
                before: createExistingHistorySnapshot(before),
                after: createExistingHistorySnapshot(after),
                metadata: {sketchId: input.sketchId},
            }),
        ];

        if (solverPatch) {
            patches.push(solverPatch);
        }

        // driving 驱动几何后同岛 driven 尺寸随同一笔事务原子重测回写。
        const remeasuredBefores = this.appendDrivenRemeasurePatches(input.sketchId, input.dimensionId, session, patches);

        try {
            await this.options.transactionPort.commitTransaction({
                label: input.label,
                commandType: `${DIMENSION_COMMAND_PREFIX}${input.commandSuffix}`,
                sketchId: input.sketchId,
                dimensionId: input.dimensionId,
                patches,
            });
        } catch (error) {
            repository.restoreDimension(before);
            remeasuredBefores.forEach((snapshot) => repository.restoreDimension(snapshot));
            session?.rollback();
            throw error;
        }

        return after;
    }

    /**
     * 追加"同岛 driven 尺寸重测"补丁（driving 驱动几何后随同一笔事务原子回写）。
     *
     * 语义：driving 尺寸创建/改值会驱动几何，同岛 driven 尺寸的测量值随之变化；
     * 本方法把 solver 会话已算好的新测量值写回 driven 聚合，并生成 update 补丁并入本次
     * 事务——保证 driven 显示随几何更新、且撤销/重做与几何一致。measuredValue 是几何派生量，
     * 不重复测量、只搬运求解结果；primaryDimensionId（本次主变更尺寸）不在此重测。
     *
     * @param sketchId 草图持久 ID。
     * @param primaryDimensionId 本次主变更尺寸持久 ID（跳过）。
     * @param session solver 会话（含 measuredValues）；无联动为 null。
     * @param patches 事务补丁数组（就地追加）。
     * @returns 被改写 driven 尺寸的 before 快照（事务失败时用于回滚恢复）。
     */
    private appendDrivenRemeasurePatches(
        sketchId: string,
        primaryDimensionId: string,
        session: SketchDimensionSolverSession | null,
        patches: EntityPatch[],
    ): readonly SketchDimensionSnapshot[] {
        const measuredValues = session?.measuredValues;

        if (!measuredValues || measuredValues.size === 0) {
            return [];
        }

        const repository = this.options.repository;
        const befores: SketchDimensionSnapshot[] = [];

        repository.listBySketch(sketchId).forEach((dimension) => {
            const before = dimension.toSnapshot();
            const measured = measuredValues.get(before.dimensionId);

            // 主变更尺寸、无新测量值、或测量值未变：不产生冗余补丁。
            if (before.dimensionId === primaryDimensionId || measured === undefined || before.measuredValue === measured) {
                return;
            }

            const after = repository.mutate(sketchId, before.dimensionId, (target) => target.updateMeasuredValue(measured));

            befores.push(before);
            patches.push(createSketchDimensionHistoryPatch({
                entityId: before.dimensionId,
                operation: 'update',
                before: createExistingHistorySnapshot(before),
                after: createExistingHistorySnapshot(after),
                metadata: {sketchId},
            }));
        });

        return befores;
    }
}
